import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS } from "../popup/popupLayoutSpec";
import Heading from "../common/Typography/Heading";
import Paragraph from "../common/Typography/Paragraph";
import AuxiliaryText from "../common/Typography/AuxiliaryText";
import SpecialText from "../common/Typography/SpecialText";
import ProfileSpecialtyChips from "../common/Profile/ProfileSpecialtyChips";
import Pro from "../common/Tags/Pro";
import {
  PUBLIC_POST_DETAIL_PROFILE_AVATAR_CLASS,
  PUBLIC_POST_DETAIL_PROFILE_AVATAR_WRAPPER_CLASS,
  PUBLIC_POST_DETAIL_PROFILE_PRO_BADGE_CLASS,
} from "../../sections/Public/shared/publicLayoutSpec";
import MainButton from "../common/MainButton/MainButton";
import StepBackButton from "../common/StepBackButton/StepBackButton";
import TextFiled from "../common/InputField/TextFiled";
import TextArea from "../common/InputField/TextArea";
import FieldDropdown from "../common/InputField/FieldDropdown";
import useAuth from "../../Hooks/useAuth";
import SecondaryHeading from "../common/Typography/SecondaryHeading";
import DropzoneUploader from "../common/DropzoneUploader/DropzoneUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { disableScroll, enableScroll } from "../../utils/scrollUtils";
import { useGraphQL } from "../../context/GraphQLProvider";
import "./InquiryForm.css";
import { 
  setField, 
  setStep, 
  setSubmitting, 
  setConfirmation, 
  setImages, 
  setAction,
  setPostData,
  setProfileData,
  resetInquiryForm 
} from "../../Redux/Slice/inquiryFormSlice";
import { CreateInquiryMutation, transformInquiryInput } from "../../services/InquiryFormService";
import { runTextPipeline } from "../../utils/contentSafety";
import Alerts from "../common/Alerts/Alerts";
import Spinner from "../common/Spinner";

// Image Container Component
const ImageContainer = React.memo(({ url, name, isDragging }) => (
  <div className="relative size-full">
    <div 
      className="size-full bg-cover bg-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200"
      style={{ 
        backgroundImage: `url(${url})`,
        opacity: isDragging ? 0.5 : 1
      }}
    />
    <div className="absolute inset-0 bg-transparent" />
  </div>
));

// Sortable Item Component
const SortableItem = ({ id, item, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }), [transform, transition, isDragging]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative group">
        <div className="size-[60px]">
          <ImageContainer 
            url={item.url || ''} 
            name={item?.file?.name || 'Uploaded image'}
            isDragging={isDragging}
          />
          <button 
            type="button" 
            onClick={() => onDelete(index)}
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <i className="fi fi-rs-cross text-[10px] text-offblack"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Validation schema for the inquiry form
const inquiryValidationSchema = Yup.object().shape({
  // Step 1: Contact method selection
  contactMethod: Yup.string()
    .oneOf(['message', 'contact', 'quote'], 'Please select a contact method')
    .required('Contact method is required'),
  
  // Message field (for both post and profile "message" option)
  message: Yup.string()
    .min(5, 'Message must be at least 5 characters')
    .max(1000, 'Message cannot exceed 1000 characters')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'message');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .when('contactMethod', {
      is: 'message',
      then: Yup.string().required('Message is required')
    }),
  
  // Quote request fields (only for profile "quote" option) — pricing allowed in jobDetails
  jobTitle: Yup.string()
    .min(5, 'Job title must be at least 5 characters')
    .max(60, 'Job title cannot exceed 60 characters')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'jobTitle');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .when('contactMethod', {
      is: 'quote',
      then: Yup.string().required('Job title is required')
    }),
  
  jobDetails: Yup.string()
    .min(5, 'Job details must be at least 5 characters')
    .max(1000, 'Job details cannot exceed 1000 characters')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'quoteMessage');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .when('contactMethod', {
      is: 'quote',
      then: Yup.string().required('Job details are required')
    }),
  
  timeline: Yup.string()
    .when('contactMethod', {
      is: 'quote',
      then: Yup.string().required('Please select when the work is needed')
    }),
  
  quoteImages: Yup.string().test('quoteImages-validation', 'Maximum 3 photos allowed', function(value) {
    if (!value || value === "" || value === "[]") return true;
    try {
      const parsedImages = JSON.parse(value);
      return Array.isArray(parsedImages) && parsedImages.length <= 3;
    } catch {
      return false;
    }
  })
});

const InquiryForm = ({ 
  isOpen, 
  onClose, 
  action = "post", // Valid values: "post" or "profile"
  postData = null, 
  profileData = null 
}) => {
  const graphqlService = useGraphQL();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const [showCrossButton, setShowCrossButton] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localImages, setLocalImages] = useState([]); // Local state for UI display
  
  // Helper function to parse stringified images and profileImages
  const parseStringifiedData = useCallback((dataString) => {
    if (!dataString) return [];
    try {
      return JSON.parse(dataString);
    } catch {
      return [];
    }
  }, []);
  
  // Get state from Redux
  const inquiryFormState = useSelector((state) => state.inquiryForm);
  const {
    currentStep,
    isSubmitting,
    showConfirmation,
    images,
    action: currentAction,
    contactMethod,
    message,
    jobTitle,
    jobDetails,
    timeline,
    quoteImages,
    senderId
  } = inquiryFormState;

  // Debug logging for flowType and action - will be moved after formik declaration

  // Timeline options for dropdown (now simple strings)
  const timelineOptions = [
    { label: "ASAP", value: "asap" },
    { label: "Within a Week", value: "within_week" },
    { label: "This Month", value: "this_month" },
    { label: "Not Sure Yet", value: "not_sure" }
  ];

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );



  const formik = useFormik({
    initialValues: {
      action: action,
      flowType: action === "post" ? "postinquiry" : "profileinquiry",
      contactMethod: "",
      message: "",
      jobTitle: "",
      jobDetails: "",
      timeline: "",
      quoteImages: "",
      senderId: auth?.user || null,
      postData: postData || null,
      profileData: profileData || null,
    },
    validationSchema: inquiryValidationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      console.log("Inquiry Form Values:", values);
      dispatch(setSubmitting(true));
      
      try {
        // Bypass API for now - directly show confirmation
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
        
        dispatch(setConfirmation(true));
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
        
        /* TODO: Uncomment when ready to integrate API
        const submissionValues = {
          ...values,
          quoteImages: localImages.length > 0 ? JSON.stringify(localImages) : "",
          postData: formik.values.action === "post" ? (postData || null) : null,
          profileData: formik.values.action === "profile" ? (profileData || null) : null,
        };
        
        const inquiryInput = transformInquiryInput(submissionValues, auth);
        const response = await CreateInquiryMutation(graphqlService, { input: inquiryInput });
        
        if (response.createInquiry?.id) {
          dispatch(setConfirmation(true));
          setTimeout(() => {
            handleClose();
          }, 3000);
        }
        */
      } catch (error) {
        console.error('Error submitting inquiry:', error);
        Alerts.error(error.message || 'Failed to submit inquiry');
      } finally {
        dispatch(setSubmitting(false));
      }
    }
  });

  // Debug logging for action
  console.log('InquiryForm state:', { 
    action, 
    currentAction, 
    currentStep
  });

  // Handle step navigation
  const handleNext = () => {
    const isPostFlow = formik.values.action === 'post';
    
    if (currentStep === 1) {
      if (isPostFlow) {
        // Post flow: Initial step -> Contact method selection
        dispatch(setStep(2));
      } else {
        // Profile flow: Contact method selection -> Next step based on method
        if (formik.values.contactMethod === 'contact') {
          dispatch(setStep(2));
        } else if (formik.values.contactMethod === 'message') {
          dispatch(setStep(2));
        } else if (formik.values.contactMethod === 'quote') {
          dispatch(setStep(2));
        }
      }
    } else if (currentStep === 2) {
      if (isPostFlow) {
        // Post flow: Contact method selection -> Next step based on method
        if (formik.values.contactMethod === 'contact') {
          dispatch(setStep(3));
        } else if (formik.values.contactMethod === 'message') {
          dispatch(setStep(3));
        } else if (formik.values.contactMethod === 'quote') {
          dispatch(setStep(3));
        }
      } else {
        // Profile flow: Already handled in step 1
        if (currentStep < getMaxStep()) {
          dispatch(setStep(currentStep + 1));
        } else {
          formik.handleSubmit();
        }
      }
    } else if (currentStep < getMaxStep()) {
      dispatch(setStep(currentStep + 1));
    } else {
      // Submit form
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      dispatch(setStep(currentStep - 1));
    }
  };

  // Get maximum step based on contact method and flow type
  const getMaxStep = () => {
    const isPostFlow = formik.values.action === 'post';
    
    if (formik.values.contactMethod === 'contact') {
      return isPostFlow ? 3 : 2; // Post: Initial -> Contact method -> Contact details | Profile: Contact method -> Contact details
    } else if (formik.values.contactMethod === 'message') {
      return isPostFlow ? 3 : 2; // Post: Initial -> Contact method -> Message | Profile: Contact method -> Message
    } else if (formik.values.contactMethod === 'quote') {
      return isPostFlow ? 6 : 5; // Post: Initial -> Contact method -> Job Title -> Job Details -> When Needed -> Photos | Profile: Contact method -> Job Title -> Job Details -> When Needed -> Photos
    }
    return isPostFlow ? 2 : 1; // Default: Post flow has initial step, Profile flow starts with contact method
  };

  // Handle checkbox changes
  const handleCheckboxChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    // Also update Redux state immediately
    dispatch(setField({ [fieldName]: value }));
  };

  // Handle text field change
  const handleTextFieldChange = (e) => {
    const { name, value } = e.target;
    formik.handleChange(e);
    dispatch(setField({ [name]: value }));
  };

  // Handle text field blur - validate field
  const handleTextFieldBlur = async (e) => {
    formik.handleBlur(e);
    // Validate the specific field
    const { name } = e.target;
    await formik.validateField(name);
  };

  // Handle dropdown change
  const handleDropdownChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    // Also update Redux state immediately
    dispatch(setField({ [fieldName]: value }));
  };

  // Handle image upload for quote images
  const handleUploadComplete = useCallback((uploadData) => {
    const newImages = Array.isArray(uploadData) ? uploadData : [uploadData];
    const processedImages = newImages.map((data, index) => ({
      name: data.fileName,
      url: data.url,
    }));
    
    const updatedImages = [...localImages, ...processedImages];
    
    // Update local state for UI
    setLocalImages(updatedImages);
    
    // Convert to stringified format for Redux
    const stringifiedImages = JSON.stringify(updatedImages);
    const stringifiedQuoteImages = JSON.stringify(updatedImages);
    
    dispatch(setImages(stringifiedImages));
    formik.setFieldValue("quoteImages", stringifiedQuoteImages);
    dispatch(setField({ quoteImages: stringifiedQuoteImages }));
  }, [localImages, formik, dispatch]);

  const deleteImage = useCallback((index) => {
    const newImages = localImages.filter((_, i) => i !== index);
    
    // Update local state for UI
    setLocalImages(newImages);
    
    // Convert to stringified format for Redux
    const stringifiedImages = JSON.stringify(newImages);
    const stringifiedQuoteImages = JSON.stringify(newImages);
    
    dispatch(setImages(stringifiedImages));
    formik.setFieldValue("quoteImages", stringifiedQuoteImages);
    dispatch(setField({ quoteImages: stringifiedQuoteImages }));
  }, [localImages, formik, dispatch]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = localImages.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = localImages.findIndex((_, index) => `item-${index}` === over.id);
      
      const newItems = arrayMove(localImages, oldIndex, newIndex);
      
      // Update local state for UI
      setLocalImages(newItems);
      
      // Convert to stringified format for Redux
      const stringifiedImages = JSON.stringify(newItems);
      const stringifiedQuoteImages = JSON.stringify(newItems);
      
      dispatch(setImages(stringifiedImages));
      formik.setFieldValue("quoteImages", stringifiedQuoteImages);
      dispatch(setField({ quoteImages: stringifiedQuoteImages }));
    }
    
    setIsDragging(false);
    setActiveId(null);
  }, [localImages, formik, dispatch]);

  const items = useMemo(() => 
    localImages.map((_, index) => `item-${index}`),
    [localImages]
  );

  // Get step title based on current step and contact method
  const getStepTitle = () => {
    const isPostFlow = formik.values.action === 'post';
    
    if (currentStep === 1) {
      if (isPostFlow) {
        return "Connecting with";
      } else {
        return "Contact Method";
      }
    }
    
    if (currentStep === 2) {
      if (isPostFlow) {
        return "Contact Method";
      } else {
        // Profile flow - handle based on contact method
        if (formik.values.contactMethod === 'contact') {
          return "Contact Details";
        } else if (formik.values.contactMethod === 'message') {
          return "Message";
        } else if (formik.values.contactMethod === 'quote') {
          return "Job Title";
        }
      }
    }
    
    if (formik.values.contactMethod === 'contact') {
      if (currentStep === 3) return "Contact Details";
    } else if (formik.values.contactMethod === 'message') {
      if (currentStep === 3) return "Message";
    } else if (formik.values.contactMethod === 'quote') {
      switch (currentStep) {
        case 3: return "Job Title";
        case 4: return "Job Details";
        case 5: return "When Needed";
        case 6: return "Photos";
        default: return "";
      }
    }
    
    return "";
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    const isPostFlow = formik.values.action === 'post';
    
    if (currentStep === 1) {
      if (isPostFlow) {
        // Initial step is always valid (just showing profile info)
        return true;
      } else {
        // Profile flow - contact method must be selected
        return !!formik.values.contactMethod && !formik.errors.contactMethod;
      }
    }
    
    if (currentStep === 2) {
      if (isPostFlow) {
        // Contact method must be selected
        return !!formik.values.contactMethod && !formik.errors.contactMethod;
      } else {
        // Profile flow - handle based on contact method
        if (formik.values.contactMethod === 'contact') {
          return true; // Contact details step is always valid
        } else if (formik.values.contactMethod === 'message') {
          return formik.values.message.length >= 5 && formik.values.message.length <= 1000 && !formik.errors.message;
        } else if (formik.values.contactMethod === 'quote') {
          return formik.values.jobTitle.length >= 5 && formik.values.jobTitle.length <= 60 && !formik.errors.jobTitle;
        }
      }
    }
    
    if (formik.values.contactMethod === 'contact') {
      if (currentStep === 3) return true; // Contact details step is always valid
    } else if (formik.values.contactMethod === 'message') {
      if (currentStep === 3) {
        return formik.values.message.length >= 5 && formik.values.message.length <= 1000 && !formik.errors.message;
      }
    } else if (formik.values.contactMethod === 'quote') {
      switch (currentStep) {
        case 3:
          return formik.values.jobTitle.length >= 5 && formik.values.jobTitle.length <= 60 && !formik.errors.jobTitle;
        case 4:
          return formik.values.jobDetails.length >= 5 && formik.values.jobDetails.length <= 1000 && !formik.errors.jobDetails;
        case 5:
          return !!formik.values.timeline && !formik.errors.timeline;
        case 6:
          return true; // Photos: optional, always valid
        default:
          return false;
      }
    }
    
    return false;
  };

  // Render step content
  const renderStepContent = () => {
    if (currentStep === 1) {
      // For post flow, show initial step. For profile flow, show contact method directly
      if (formik.values.action === 'post') {
        return renderInitialStep();
      } else {
        return renderContactMethodStep();
      }
    }
    
    if (currentStep === 2) {
      // For post flow, this is contact method. For profile flow, this is the first step after contact method
      if (formik.values.action === 'post') {
        return renderContactMethodStep();
      } else {
        // Profile flow - handle based on contact method
        if (formik.values.contactMethod === 'contact') {
          return renderContactDetailsStep();
        } else if (formik.values.contactMethod === 'message') {
          return renderMessageStep();
        } else if (formik.values.contactMethod === 'quote') {
          return renderJobTitleStep();
        }
      }
    }
    
    if (formik.values.contactMethod === 'contact') {
      if (currentStep === 3) return renderContactDetailsStep();
    } else if (formik.values.contactMethod === 'message') {
      if (currentStep === 3) return renderMessageStep();
    } else if (formik.values.contactMethod === 'quote') {
      switch (currentStep) {
        case 3: return renderJobTitleStep();
        case 4: return renderJobDetailsStep();
        case 5: return renderTimelineStep();
        case 6: return renderQuotePhotosStep();
        default: return null;
      }
    }
    
    return null;
  };

  // Step 1: Initial Step - Show profile info for post flow only
  const renderInitialStep = () => {
    // Only show this step for post flow
    if (formik.values.action !== 'post' || !postData) {
      return null;
    }

    return (
      <div className="w-full flex justify-center">
        <div className="mb-10 w-full">
          <div className="flex w-full justify-center pb-2.5">
            <div className={PUBLIC_POST_DETAIL_PROFILE_AVATAR_WRAPPER_CLASS}>
              <img
                src={postData?.user?.profilePicture || require('../../assets/img/default-image.jpg')}
                alt={postData?.user?.businessName}
                className={PUBLIC_POST_DETAIL_PROFILE_AVATAR_CLASS}
              />
              {postData?.user?.userPlan?.plan?.name?.toLowerCase() === 'pro' && (
                <span className={PUBLIC_POST_DETAIL_PROFILE_PRO_BADGE_CLASS}>
                  <Pro className="bg-primaryblue text-white" />
                </span>
              )}
            </div>
          </div>
          <div className="mt-2 flex flex-col items-center gap-2 w-full">
            <SpecialText variant="ProfileName">
              {postData?.user?.businessName}
            </SpecialText>
            <SpecialText variant="ProfileMeta">
              {postData?.user?.serviceCategory?.name || 'Category'}
            </SpecialText>
            <ProfileSpecialtyChips />
            <SpecialText variant="ProfileMicro">
              {postData?.user?.location || ''}
            </SpecialText>
            <SpecialText variant="Date">
              Member since {postData?.user?.startDate ? new Date(postData.user.startDate).getFullYear() : 'N/A'}
            </SpecialText>
          </div>
        </div>
      </div>
    );
  };

  // Step 2: Contact Method Selection
  const renderContactMethodStep = () => {
    const isPost = formik.values.action === 'post';
    return (
      <div className="w-full">
        {/* <div className="mb-4">
          <Paragraph variant="MainParagraph" className="text-offblack text-left">
            How would you like to get in touch?
          </Paragraph>
        </div> */}
        <div className="flex flex-col gap-4">
          <div
            onClick={() => handleCheckboxChange('contactMethod', 'message')}
            className={`rounded-[4px] flex items-center justify-center w-full h-[48px] p-4 cursor-pointer border border-solid transition-colors duration-200 ${formik.values.contactMethod === 'message' ? 'bg-primaryblue text-white border-primaryblue' : 'bg-white text-offblack border-[#C0C0C0]'}`}
          >
            <span className="input-label font-inter text-inherit text-left w-full">
              Send a Message
            </span>
          </div>
          <div
            onClick={() => handleCheckboxChange('contactMethod', isPost ? 'contact' : 'quote')}
            className={`rounded-[4px] flex items-center justify-center w-full h-[48px] p-4 cursor-pointer border border-solid transition-colors duration-200 ${formik.values.contactMethod === (isPost ? 'contact' : 'quote') ? 'bg-primaryblue text-white border-primaryblue' : 'bg-white text-offblack border-[#C0C0C0]'}`}
          >
            <span className="input-label font-inter text-inherit text-left w-full">
              {isPost ? 'View Contact Details' : 'Request a Quote'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Step 2: Contact Details (for "contact" method in post flow)
  const renderContactDetailsStep = () => {
    // This should show business contact information
    // For now, using placeholder data - will be replaced with actual business data
    const contactData = [
      { icon: "fi-br-smartphone", value: "0412 345 678" },
      { icon: "fi-sr-phone-call", value: "03 1234 5678" },
      { icon: "fi-sr-envelope", value: "tradesposter@gmail.com" },
    ];

    return (
      <div className="w-full flex justify-center">
        <div className="flex flex-col gap-4 items-start w-[150px]">
          {contactData.map((item, idx) => {
            let href = null;
            if (item.icon.includes('envelope')) {
              href = `mailto:${item.value}`;
            } else if (item.icon.includes('phone') || item.icon.includes('smartphone')) {
              href = `tel:${item.value.replace(/\s+/g, '')}`;
            }
            return href ? (
              <a
                key={idx}
                href={href}
                className="flex items-center gap-3 group transition-colors duration-200"
                onClick={() => {
                  // Auto-close popup when contact link is clicked
                  setTimeout(() => handleClose(), 100);
                }}
              >
                <i className={`fi ${item.icon} text-base flex items-center`}></i>
                <Paragraph variant={"BodyCompact"} className="group-hover:text-primaryblue transition-colors duration-200">{item.value}</Paragraph>
              </a>
            ) : (
              <div key={idx} className="flex items-center gap-3">
                <i className={`fi ${item.icon} text-base flex items-center`}></i>
                <Paragraph variant={"BodyCompact"}>{item.value}</Paragraph>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Step 2: Message (for "message" method)
  const renderMessageStep = () => (
    <div className="w-full">
      <div className="relative">
        <TextArea
          variant="general"
          fullWidth
          label={null}
          placeholder="Write your message"
          name="message"
          value={formik.values.message}
          onChange={handleTextFieldChange}
          onBlur={handleTextFieldBlur}
          className="min-h-[120px] resize-none"
          style={{ resize: 'none' }}
        />
      </div>
      {formik.touched.message && formik.errors.message && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.message}
        </SpecialText>
      )}
      <div className="mt-2">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          5-1000 characters
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 2: Job Title (for "quote" method)
  const renderJobTitleStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        placeholder="Enter a short job title"
        name="jobTitle"
        value={formik.values.jobTitle}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.jobTitle && formik.errors.jobTitle ? formik.errors.jobTitle : null}
      />
      <div className="mt-2">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          5-60 characters
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 3: Job Details (for "quote" method)
  const renderJobDetailsStep = () => (
    <div className="w-full">
      <div className="relative">
        <TextArea
          variant="general"
          fullWidth
          label={null}
          placeholder="Describe the job"
          name="jobDetails"
          value={formik.values.jobDetails}
          onChange={handleTextFieldChange}
          onBlur={handleTextFieldBlur}
          className="min-h-[120px] resize-none"
          style={{ resize: 'none' }}
        />
      </div>
      {formik.touched.jobDetails && formik.errors.jobDetails && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.jobDetails}
        </SpecialText>
      )}
      <div className="mt-2">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          5-1000 characters
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 4: Timeline
  const renderTimelineStep = () => (
    <div className="w-full">
      <FieldDropdown
        variant="medium"
        fullWidth
        value={formik.values.timeline}
        onChange={(e) => handleDropdownChange('timeline', e.value)}
        onBlur={() => formik.setFieldTouched('timeline', true)}
        options={timelineOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select timeline"
        panelClassName="custom-dropdown-panel !z-[9999]"
        className="inquiry-timeline-dropdown"
        error={formik.touched.timeline && formik.errors.timeline ? formik.errors.timeline : null}
      />
    </div>
  );

  // Step 5: Quote Photos (for "quote" method - optional)
  const renderQuotePhotosStep = () => (
    <div className="w-full">
      {/* <div className="mb-4">
        <Paragraph variant="BodyCompact" className="text-offblack">
          You can include maximum 3 photos
        </Paragraph>
      </div> */}
      <div className="w-full">
        {/* Uploaded Images */}
        {localImages.length > 0 && (
          <div className="mt-1 mb-5 w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-7">
                  {localImages.map((item, index) => (
                    <SortableItem
                      key={`item-${index}`}
                      id={`item-${index}`}
                      item={item}
                      index={index}
                      onDelete={deleteImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        <DropzoneUploader
          isImage={true}
          maxFiles={3}
          loadingType="progress"
          onUploadComplete={handleUploadComplete}
          currentFileCount={localImages.length}
          applyFieldStyles={false}
          fullWidth
        />
      </div>
      {formik.touched.quoteImages && formik.errors.quoteImages && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.quoteImages}
        </SpecialText>
      )}
    </div>
  );

  // Handle popup close
  const handleClose = () => {
    onClose();
    formik.resetForm();
    dispatch(resetInquiryForm());
    // Reset local images state
    setLocalImages([]);
  };

  // Track if we've already initialized to prevent re-running on image updates
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form with props when component mounts
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      // Reset the form state first to ensure clean start
      dispatch(resetInquiryForm());
      
      // Set action and data in Redux
      dispatch(setAction(action));
      if (postData) {
        dispatch(setPostData(postData));
      }
      if (profileData) {
        dispatch(setProfileData(profileData));
      }
      
      // Initialize step to 1
      dispatch(setStep(1));
      
      // Initialize user data if authenticated
      if (auth) {
        const userData = {
          name: auth.user?.firstName ? `${auth.user.firstName} ${auth.user.lastName || ''}`.trim() : "",
          email: auth.user?.email || "",
          mobile: "",
          senderId: auth.user || null,
        };
        dispatch(setField(userData));
      }
      
      // Initialize local images from stringified images in Redux
      if (images && typeof images === 'string') {
        try {
          const parsedImages = parseStringifiedData(images);
          if (Array.isArray(parsedImages)) {
            setLocalImages(parsedImages);
          }
        } catch (error) {
          console.warn('Failed to parse images:', error);
        }
      }
      
      // Also initialize from quoteImages if images is empty
      if ((!images || !images.length) && quoteImages && typeof quoteImages === 'string') {
        try {
          const parsedQuoteImages = parseStringifiedData(quoteImages);
          if (Array.isArray(parsedQuoteImages)) {
            setLocalImages(parsedQuoteImages);
            // Update images in Redux as well
            dispatch(setImages(JSON.stringify(parsedQuoteImages)));
          }
        } catch (error) {
          console.warn('Failed to parse quoteImages:', error);
        }
      }
      
      setHasInitialized(true);
    }
    
    // Reset initialization flag when popup closes
    if (!isOpen && hasInitialized) {
      setHasInitialized(false);
    }
  }, [isOpen, hasInitialized, action, postData, profileData, auth, dispatch, images, quoteImages, parseStringifiedData]);

  // Note: Removed the formik-Redux sync useEffect to prevent infinite loops
  // The form values are now synced directly in the handlers (handleCheckboxChange, handleDropdownChange, etc.)

  // Update body overflow when popup is open
  useEffect(() => {
    if (isOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [isOpen]);

  // Cleanup effect to ensure scrolling is restored on unmount
  useEffect(() => {
    return () => {
      enableScroll();
    };
  }, []);

  if (!isOpen) return null;

  // Render confirmation step
  if (showConfirmation) {
    return (
      <Popup
        setTrigger={() => handleClose()}
        ShowCrossButton={showCrossButton}
        onClose={handleClose}
        higherZIndex={true}
      >
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading
              variant={"h6"}
              className={
                "flex items-center justify-center text-center text-offblack"
              }
            >
              Sent
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <Paragraph
                    variant={"MainParagraph"}
                    className={"text-offblack text-center"}
                  >
                    ABC Plumbing will reply in your Messages inbox.
                  </Paragraph>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      setTrigger={() => handleClose()}
      ShowCrossButton={showCrossButton}
      onClose={handleClose}
      higherZIndex={true}
    >
      {isSubmitting && <Spinner />}
      <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <Heading variant="h6" className="flex items-center justify-center text-center">
            {getStepTitle()}
          </Heading>
          <div className="mt-10 xl:mt-8 w-full">
            {renderStepContent()}
            <div className={`flex items-center mt-10 w-full ${currentStep === 1 ? "justify-end" : (formik.values.contactMethod === 'contact' && currentStep === 3 ? "justify-center" : "justify-between")}`}>
              {currentStep > 1 && (
                <div className={formik.values.contactMethod === 'contact' && currentStep === 3 ? "flex w-[150px]" : ""}>
                  <StepBackButton onClick={handleBack} />
                </div>
              )}
              {!(formik.values.contactMethod === 'contact' && currentStep === 3) && (
                <MainButton
                  variant="small"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid() || isSubmitting}
                >
                  {(() => {
                    const isSubmitStep = currentStep === getMaxStep();
                    return isSubmitStep ? (isSubmitting ? 'Send' : 'Send') : 'Next';
                  })()}
                </MainButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default InquiryForm; 